"use client";

import React from "react";
import Link from "next/link";
import { Breadcrumbs, BreadcrumbItem } from "./Breadcrumbs";
import { ShieldCheck, Calendar, FileText, ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

export interface LegalSection {
  number: string;
  title: string;
  paragraphs: string[];
}

export interface LegalDocTemplateProps {
  breadcrumbs: BreadcrumbItem[];
  title: string;
  subtitle: string;
  effectiveDate: string;
  version: string;
  sections: LegalSection[];
  companyDetails: {
    legalName: string;
    inn: string;
    ogrn: string;
    address: string;
    email: string;
    phone: string;
  };
}

export function LegalDocTemplate({
  breadcrumbs,
  title,
  subtitle,
  effectiveDate,
  version,
  sections,
  companyDetails,
}: LegalDocTemplateProps) {
  return (
    <div className="pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-4 rounded-3xl border border-gray-200/90 dark:border-zinc-800/90 bg-white dark:bg-[#0E121E] p-6 sm:p-10 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-gray-100 dark:border-zinc-800/80">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-800/40 text-[11px] font-mono uppercase tracking-wider text-[#0a3d90] dark:text-[#55a9ef]">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Официальный документ</span>
            </div>

            <div className="flex items-center gap-4 text-xs font-mono text-gray-700 dark:text-zinc-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Действует с: {effectiveDate}</span>
              </span>
              <span>Редакция: {version}</span>
            </div>
          </div>

          <div className="mt-6">
            <h1 className="text-2xl sm:text-4xl font-black text-gray-950 dark:text-white tracking-tight">
              {title}
            </h1>
            <p className="mt-3 text-sm sm:text-base text-gray-600 dark:text-zinc-300 leading-relaxed max-w-3xl">
              {subtitle}
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl border border-gray-100 dark:border-zinc-800 bg-gray-50/70 dark:bg-zinc-900/40">
              <span className="text-[11px] font-mono text-gray-700 dark:text-zinc-400 block uppercase">
                Оператор данных
              </span>
              <span className="mt-1 text-xs sm:text-sm font-bold text-gray-950 dark:text-white block">
                {companyDetails.legalName}
              </span>
            </div>

            <div className="p-4 rounded-xl border border-gray-100 dark:border-zinc-800 bg-gray-50/70 dark:bg-zinc-900/40">
              <span className="text-[11px] font-mono text-gray-700 dark:text-zinc-400 block uppercase">
                Правовая база
              </span>
              <span className="mt-1 text-xs sm:text-sm font-bold text-gray-950 dark:text-white block">
                ФЗ №152 «О персональных данных»
              </span>
            </div>

            <div className="p-4 rounded-xl border border-gray-100 dark:border-zinc-800 bg-gray-50/70 dark:bg-zinc-900/40">
              <span className="text-[11px] font-mono text-gray-700 dark:text-zinc-400 block uppercase">
                Защита каналов
              </span>
              <span className="mt-1 text-xs sm:text-sm font-bold text-emerald-800 dark:text-emerald-400 block">
                SSL 256-bit шифрование
              </span>
            </div>
          </div>

          <div className="mt-12 space-y-10">
            {sections.map((section) => (
              <div key={section.number} className="space-y-4">
                <div className="flex items-baseline gap-3 pb-2 border-b border-gray-100 dark:border-zinc-800/80">
                  <span className="text-base font-mono font-black text-[#0a3d90] dark:text-[#55a9ef]">
                    {section.number}
                  </span>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-950 dark:text-white">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-3 text-xs sm:text-sm text-gray-600 dark:text-zinc-300 leading-relaxed">
                  {section.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 pt-8 border-t border-gray-200/90 dark:border-zinc-800/90 rounded-2xl bg-gray-50/60 dark:bg-zinc-900/50 p-6">
            <h3 className="text-sm font-bold text-gray-950 dark:text-white uppercase font-mono tracking-wider mb-4">
              Реквизиты оператора персональных данных
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-gray-600 dark:text-zinc-300">
              <div className="space-y-1.5">
                <div>
                  <strong className="text-gray-900 dark:text-white">Наименование:</strong>{" "}
                  {companyDetails.legalName}
                </div>
                <div>
                  <strong className="text-gray-900 dark:text-white">ИНН / ОГРН:</strong>{" "}
                  {companyDetails.inn} / {companyDetails.ogrn}
                </div>
                <div>
                  <strong className="text-gray-900 dark:text-white">Адрес:</strong>{" "}
                  {companyDetails.address}
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-[#0a3d90] dark:text-[#55a9ef]" />
                  <span>{companyDetails.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-[#0a3d90] dark:text-[#55a9ef]" />
                  <span>{companyDetails.phone}</span>
                </div>
                <div className="text-[11px] text-gray-700 dark:text-zinc-400 mt-2">
                  По вопросам отзыва согласия направьте запрос на официальную почту.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

